/**
 * `company_selected` capture + company group identification (SD-10914).
 *
 * The event used to fire only from the manual company pickers, so the
 * activation funnel's "Company Selected" step missed almost every session:
 * single-company users get their company auto-selected and never touch a
 * picker. Capture is therefore two-path:
 *  - "manual" — an explicit pick in a company picker; always captured.
 *  - "auto" — the session's effective company, fired by the gating hook once
 *    capture opts in; deduped per (tab session, company) so gating re-runs
 *    (company refetches, user changes) don't inflate counts.
 *
 * `identifyCompanyGroup` links subsequent events to the company as a PostHog
 * group (`company` group type), enabling per-company B2B aggregation.
 */

import type { PostHog } from "posthog-js";

type CompanySelectionClient = Pick<
  PostHog,
  "capture" | "group" | "has_opted_out_capturing"
>;

const SELECTED_MARKER_PREFIX = "ph_company_selected:";

// Per-page-load dedup — the sole guard when sessionStorage is unavailable
// (privacy mode), degrading to once-per-company per load.
const selectedThisLoad = new Set<string>();

function hasSessionMarker(companyId: string): boolean {
  if (selectedThisLoad.has(companyId)) {
    return true;
  }
  try {
    return sessionStorage.getItem(SELECTED_MARKER_PREFIX + companyId) !== null;
  } catch {
    return false;
  }
}

function setSessionMarker(companyId: string): void {
  selectedThisLoad.add(companyId);
  try {
    sessionStorage.setItem(SELECTED_MARKER_PREFIX + companyId, "1");
  } catch {
    // sessionStorage unavailable — the module Set already covers this load.
  }
}

type CaptureCompanySelectedProps = {
  companyId: string | undefined;
  companyName?: string;
  /**
   * "manual" — explicit pick in a company picker; always captured.
   * "auto" — the session's effective company; captured once per
   * (tab session, company).
   */
  method: "manual" | "auto";
  /** UI surface for manual picks (e.g. "insights_onboarding"). */
  source?: string;
};

type CompanySelectedEventProperties = {
  company_id: string;
  company_name: string | undefined;
  method: CaptureCompanySelectedProps["method"];
  source?: string;
};

export function captureCompanySelected(
  client: CompanySelectionClient,
  { companyId, companyName, method, source }: CaptureCompanySelectedProps,
): void {
  // While opted out the SDK silently drops the capture — bail WITHOUT setting
  // the session marker, so the gating hook's post-opt-in "auto" pass still
  // fires for this company instead of deduping against an event that never
  // recorded (same trap as pageviewTracker's dedup key).
  if (!companyId || client.has_opted_out_capturing()) {
    return;
  }
  if (method === "auto" && hasSessionMarker(companyId)) {
    return;
  }
  // Associate the company group BEFORE capturing so the event carries the
  // correct `$groups`. Manual picks fire synchronously from picker handlers —
  // ahead of the gating hook's async `identifyCompanyGroup` — so without this
  // the event would land under the *previous* company's group, misattributing
  // explicit switches in B2B group reporting. For the "auto" path (the gating
  // hook already identified immediately before) this is a no-op: the SDK dedups
  // unchanged group state.
  identifyCompanyGroup(client, companyId, companyName);
  const properties: CompanySelectedEventProperties = {
    company_id: companyId,
    company_name: companyName,
    method,
  };
  if (source) properties.source = source;
  client.capture("company_selected", properties);
  setSessionMarker(companyId);
}

/**
 * Associate the active company as a PostHog group so every subsequent event
 * in the session carries it. The SDK dedups internally (group calls with
 * unchanged key/properties don't re-send), so this is safe on every gating
 * re-run.
 */
export function identifyCompanyGroup(
  client: CompanySelectionClient,
  companyId: string | undefined,
  companyName: string | undefined,
): void {
  if (!companyId || client.has_opted_out_capturing()) {
    return;
  }
  client.group("company", companyId, companyName ? { name: companyName } : {});
}

/**
 * Clear the per-(tab session, company) auto-dedup markers. Call alongside
 * `posthog.reset()` on any identity boundary (logout, super reset): the markers
 * gate the once-per-session auto `company_selected`, so a marker left behind
 * lets a second identity in the same tab (shared browser, re-login) silently
 * skip the activation funnel's Company Selected step.
 */
export function clearCompanySelectionDedup(): void {
  selectedThisLoad.clear();
  try {
    // Iterate backwards — removeItem shifts subsequent indices.
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(SELECTED_MARKER_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {
    // sessionStorage unavailable — the in-memory Set is already cleared.
  }
}

/** Test-only: reset module state between cases. */
export function resetCompanySelectionForTests(): void {
  selectedThisLoad.clear();
}
